import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AccountDetail, AIReplySettings, AccountRuntimeStatus, AccountVerificationStatus } from '../types';
import {
  getAccountDetails,
  updateAccountStatus,
  deleteAccount,
  generateQRLogin,
  checkQRLoginStatus,
  updateAccountRemark,
  updateAccountAutoConfirm,
  updateAccountPauseDuration,
  updateAccountCookie,
  updateAccountLoginInfo,
  updateAccountAISettings,
  getAllAISettings,
  getAccountAISettings,
  getAccountRuntimeStatuses,
  issueAccountVerificationBridgeTicket,
  startLocalAccountVerificationBridge,
  completeLocalAccountVerificationBridge,
  cancelLocalAccountVerificationBridge,
  resumeAccountVerification,
  requestAccountVerificationChallenge,
  reconnectAccountAfterAuthorization,
  cancelAccountVerification
} from '../services/api';
import {
  Plus, Power, Edit2, Trash2, QrCode, X, Check, Loader2,
  MessageSquare, RefreshCw, Save, User, Clock, MessageCircle,
  Upload, Key, Eye, EyeOff, Bot, Settings, AlertTriangle, Wifi, WifiOff, CircleCheck
} from 'lucide-react';

type ModalType = 'edit' | 'ai-settings' | null;

type ManualVerification = {
  account: AccountDetail;
  session: AccountVerificationStatus;
  ticket: string;
};

const AccountList: React.FC = () => {
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [runtimeStatuses, setRuntimeStatuses] = useState<Record<string, AccountRuntimeStatus>>({});
  const [statusDetails, setStatusDetails] = useState<{ account: AccountDetail; status: AccountRuntimeStatus } | null>(null);
  const [manualVerification, setManualVerification] = useState<ManualVerification | null>(null);
  const [verificationBusy, setVerificationBusy] = useState(false);
  const [verificationResumeBusy, setVerificationResumeBusy] = useState(false);
  const runtimeStatusRequestId = useRef(0);
  const [loading, setLoading] = useState(true);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [qrStatus, setQrStatus] = useState<string>('pending');
  const [qrMessage, setQrMessage] = useState<string>('');
  const qrPollTimer = useRef<number | null>(null);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [editingAccount, setEditingAccount] = useState<AccountDetail | null>(null);

  // 编辑表单状态
  const [editForm, setEditForm] = useState({
    remark: '',
    cookie: '',
    auto_confirm: false,
    pause_duration: 0,
    username: '',
    login_password: '',
    show_browser: false,
    showLoginPassword: false,
  });

  // AI设置表单状态
  const [aiSettings, setAiSettings] = useState<AIReplySettings>({
    ai_enabled: false,
    model_name: '',
    base_url: '',
    max_discount_percent: 10,
    max_discount_amount: 100,
    max_bargain_rounds: 3,
    custom_prompts: '',
  });
  const [saving, setSaving] = useState(false);

  const formatStatusTime = (value?: string | null) => {
    if (!value) return '暂无记录';
    return new Date(value).toLocaleString('zh-CN', {
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
    }).replace(/\//g, '-');
  };

  const statusPresentation = (status?: AccountRuntimeStatus) => {
    switch (status?.status) {
      case 'healthy': return { className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CircleCheck };
      case 'reconnecting': return { className: 'bg-amber-50 text-amber-700 border-amber-200', icon: Wifi };
      case 'authorization_required': return { className: 'bg-sky-50 text-sky-700 border-sky-200', icon: Key };
      case 'verification_required': return { className: 'bg-orange-50 text-orange-700 border-orange-200', icon: AlertTriangle };
      case 'connection_error': return { className: 'bg-red-50 text-red-700 border-red-200', icon: WifiOff };
      case 'direct_im_unavailable': return { className: 'bg-violet-50 text-violet-700 border-violet-200', icon: WifiOff };
      case 'official_channel_ready': return { className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Key };
      case 'disabled': return { className: 'bg-gray-100 text-gray-600 border-gray-200', icon: Power };
      default: return { className: 'bg-slate-50 text-slate-600 border-slate-200', icon: WifiOff };
    }
  };

  const pauseResumeHint = useMemo(() => {
    const minutes = Math.max(0, Number(editForm.pause_duration) || 0);
    if (minutes === 0) return '已关闭人工接管后的暂停：人工回复后，AI 会继续正常处理该会话。';
    const resumeAt = new Date(Date.now() + minutes * 60 * 1000);
    const timestamp = resumeAt.toLocaleString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
    }).replace(/\//g, '-');
    return `若现在人工回复买家，AI 将暂停该会话，并预计于 ${timestamp} 恢复。保存此项不会立即暂停账号。`;
  }, [editForm.pause_duration]);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const data = await getAccountDetails();

      // 获取所有账号的AI设置
      let allAISettings: Record<string, AIReplySettings> = {};
      try {
        allAISettings = await getAllAISettings();
      } catch (e) {
        console.error('Failed to load AI settings:', e);
      }

      // 合并AI设置到账号数据
      const accountsWithAI = data.map(account => ({
        ...account,
        ai_enabled: allAISettings[account.id]?.ai_enabled ?? false,
        model_name: allAISettings[account.id]?.model_name ?? '',
        base_url: allAISettings[account.id]?.base_url ?? '',
        max_discount_percent: allAISettings[account.id]?.max_discount_percent ?? 10,
        max_discount_amount: allAISettings[account.id]?.max_discount_amount ?? 100,
        max_bargain_rounds: allAISettings[account.id]?.max_bargain_rounds ?? 3,
        custom_prompts: allAISettings[account.id]?.custom_prompts ?? '',
      }));

      setAccounts(accountsWithAI);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRuntimeStatuses = async (): Promise<Record<string, AccountRuntimeStatus> | null> => {
    const requestId = ++runtimeStatusRequestId.current;
    try {
      const statuses = await getAccountRuntimeStatuses();
      if (requestId === runtimeStatusRequestId.current) {
        setRuntimeStatuses(statuses);
        setStatusDetails((current) => {
          if (!current || !statuses[current.account.id]) return current;
          return { ...current, status: statuses[current.account.id] };
        });
      }
      return statuses;
    } catch (error) {
      console.error('Failed to load account runtime status:', error);
      return null;
    }
  };

  useEffect(() => {
    loadAccounts();
    loadRuntimeStatuses();
    const timer = window.setInterval(loadRuntimeStatuses, 20000);
    return () => window.clearInterval(timer);
  }, []);

  const openManualVerification = async (account: AccountDetail) => {
    setVerificationBusy(true);
    try {
      const ticket = await issueAccountVerificationBridgeTicket(account.id);
      const session = await startLocalAccountVerificationBridge(ticket.ticket);
      if (!session.available) {
        await loadRuntimeStatuses();
        alert(session.message);
        return;
      }
      setStatusDetails(null);
      setManualVerification({ account, session, ticket: ticket.ticket });
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Windows 验证桥接服务未启动。');
    } finally {
      setVerificationBusy(false);
    }
  };

  const requestManualVerification = async () => {
    if (!statusDetails) return;
    setVerificationBusy(true);
    try {
      const result = await requestAccountVerificationChallenge(statusDetails.account.id);
      await loadRuntimeStatuses();
      if (result.result === 'challenge_ready') {
        await openManualVerification(statusDetails.account);
        return;
      }
      alert(result.message);
    } catch (error) {
      alert(error instanceof Error ? error.message : '请求验证页面失败，请稍后重试。');
    } finally {
      setVerificationBusy(false);
    }
  };

  const closeManualVerification = async () => {
    if (!manualVerification) return;
    const accountId = manualVerification.account.id;
    const ticket = manualVerification.ticket;
    setManualVerification(null);
    try {
      await cancelLocalAccountVerificationBridge(ticket);
    } catch {
      // A closed local bridge has no session to cancel.
    }
    try {
      await cancelAccountVerification(accountId);
    } catch {
      // The server expires short-lived verification sessions automatically.
    }
  };

  const finishManualVerification = async () => {
    if (!manualVerification) return;
    setVerificationBusy(true);
    try {
      const result = await completeLocalAccountVerificationBridge(manualVerification.ticket);
      setManualVerification(null);
      await loadRuntimeStatuses();
      alert(result.message);
    } catch (error) {
      alert(error instanceof Error ? error.message : '验证信息保存失败，请稍后重试。');
    } finally {
      setVerificationBusy(false);
    }
  };

  const resumeAfterVerification = async () => {
    if (!statusDetails) return;
    const confirmed = window.confirm(
      '人工恢复账号只应在您已通过闲鱼官方客户端处理完平台提示后执行。请确认已处理完成，是否继续？'
    );
    if (!confirmed) return;

    setVerificationResumeBusy(true);
    try {
      const result = await resumeAccountVerification(statusDetails.account.id);
      if (!result.success) throw new Error(result.message || '账号恢复失败，请稍后重试。');
      const refreshed = await loadRuntimeStatuses();
      setStatusDetails(null);
      alert(refreshed
        ? (result.message || '账号已恢复运行。')
        : `${result.message || '账号已恢复运行。'} 状态刷新失败，请稍后点击刷新状态。`
      );
    } catch (error) {
      alert(error instanceof Error ? error.message : '账号恢复失败，请稍后重试。');
    } finally {
      setVerificationResumeBusy(false);
    }
  };

  const reconnectAfterAuthorization = async () => {
    if (!statusDetails) return;
    const statusBeforeProbe = statusDetails.status.status;
    const confirmed = window.confirm(
      statusBeforeProbe === 'connection_error'
        ? '请先检查本机网络与平台访问是否正常。继续后，系统只会进行一次受控连接探测。'
        : '请先在闲鱼官方客户端完成重新授权。继续后，系统只会进行一次聊天授权探测。'
    );
    if (!confirmed) return;

    setVerificationResumeBusy(true);
    try {
      const result = await reconnectAccountAfterAuthorization(statusDetails.account.id);
      if (!result.success) throw new Error(result.message || '重新连接失败，请稍后重试。');
      const refreshed = await loadRuntimeStatuses();
      setStatusDetails(null);
      const resultMessage = !result.resumed && result.message.includes('未处于')
        ? '账号当前已不处于需要手工恢复的暂停状态，本次未重复执行连接操作。请刷新状态确认最新结果。'
        : result.message;
      alert(refreshed
        ? resultMessage
        : `${resultMessage} 状态刷新失败，请稍后点击刷新状态。`
      );
    } catch (error) {
      alert(error instanceof Error ? error.message : '重新连接失败，请稍后重试。');
    } finally {
      setVerificationResumeBusy(false);
    }
  };

  const handleToggle = async (id: string, currentStatus: boolean) => {
    await updateAccountStatus(id, !currentStatus);
    loadAccounts();
  };

  const handleDelete = async (id: string) => {
    if (confirm('确认删除该账号吗？')) {
      await deleteAccount(id);
      loadAccounts();
    }
  };

  const openEditModal = (account: AccountDetail) => {
    setEditingAccount(account);
    setEditForm({
      remark: account.remark || account.note || '',
      cookie: account.cookie || account.value || '',
      auto_confirm: account.auto_confirm || false,
      pause_duration: account.pause_duration || 0,
      username: account.username || '',
      login_password: account.login_password || '',
      show_browser: account.show_browser || false,
      showLoginPassword: false,
    });
    setActiveModal('edit');
  };

  const openAIModal = async (account: AccountDetail) => {
    setEditingAccount(account);
    setSaving(true);
    try {
      const settings = await getAccountAISettings(account.id);
      setAiSettings({
        ai_enabled: settings.ai_enabled ?? false,
        model_name: settings.model_name ?? '',
        base_url: settings.base_url ?? '',
        credential_configured: settings.credential_configured ?? false,
        max_discount_percent: settings.max_discount_percent ?? 10,
        max_discount_amount: settings.max_discount_amount ?? 100,
        max_bargain_rounds: settings.max_bargain_rounds ?? 3,
        custom_prompts: settings.custom_prompts ?? '',
      });
    } catch (e) {
      console.error('Failed to load AI settings:', e);
    } finally {
      setSaving(false);
    }
    setActiveModal('ai-settings');
  };

  const handleSaveEdit = async () => {
    if (!editingAccount) return;
    setSaving(true);

    try {
      const promises: Promise<any>[] = [];

      // 更新备注
      if (editForm.remark !== (editingAccount.remark || editingAccount.note || '')) {
        promises.push(updateAccountRemark(editingAccount.id, editForm.remark));
      }

      // 更新Cookie
      if (editForm.cookie && editForm.cookie !== (editingAccount.cookie || editingAccount.value || '')) {
        promises.push(updateAccountCookie(editingAccount.id, editForm.cookie));
      }

      // 更新自动确认
      if (editForm.auto_confirm !== editingAccount.auto_confirm) {
        promises.push(updateAccountAutoConfirm(editingAccount.id, editForm.auto_confirm));
      }

      // 更新暂停时长
      if (editForm.pause_duration !== (editingAccount.pause_duration || 0)) {
        promises.push(updateAccountPauseDuration(editingAccount.id, editForm.pause_duration));
      }

      // 更新登录信息
      if (
        editForm.username !== (editingAccount.username || '') ||
        editForm.login_password !== (editingAccount.login_password || '') ||
        editForm.show_browser !== (editingAccount.show_browser || false)
      ) {
        promises.push(updateAccountLoginInfo(editingAccount.id, {
          username: editForm.username,
          login_password: editForm.login_password,
          show_browser: editForm.show_browser,
        }));
      }

      await Promise.all(promises);
      setActiveModal(null);
      loadAccounts();
    } catch (error) {
      console.error('更新账号失败:', error);
      alert('更新失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAISettings = async () => {
    if (!editingAccount) return;

    if (aiSettings.ai_enabled && !aiSettings.model_name.trim()) {
      alert('启用 AI 自动回复前，请填写模型名称');
      return;
    }

    if (aiSettings.ai_enabled && !aiSettings.base_url.trim()) {
      alert('启用 AI 自动回复前，请填写模型接口地址');
      return;
    }

    setSaving(true);

    try {
      await updateAccountAISettings(editingAccount.id, aiSettings);
      setActiveModal(null);
      loadAccounts();
    } catch (error) {
      console.error('更新AI设置失败:', error);
      alert('更新失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const stopQRPolling = () => {
    if (qrPollTimer.current !== null) {
      window.clearTimeout(qrPollTimer.current);
      qrPollTimer.current = null;
    }
  };

  const verificationActionBusy = verificationBusy || verificationResumeBusy;

  const closeQRModal = () => {
    stopQRPolling();
    setShowQRModal(false);
  };

  const startQRLogin = async () => {
    stopQRPolling();
    setShowQRModal(true);
    setQrStatus('loading');
    setQrMessage('');
    try {
      const res = await generateQRLogin();
      if (res.success && res.qr_code_url && res.session_id) {
        setQrCodeUrl(res.qr_code_url);
        setQrStatus('waiting');
        const poll = async () => {
          try {
            const statusRes = await checkQRLoginStatus(res.session_id!);
            setQrMessage(statusRes.message || '');
            if (statusRes.status === 'success' || statusRes.status === 'already_processed') {
              stopQRPolling();
              setQrStatus('success');
              loadAccounts();
              loadRuntimeStatuses();
              window.setTimeout(closeQRModal, 800);
              return;
            }
            if (statusRes.status === 'expired' || statusRes.status === 'error' || statusRes.status === 'verification_required') {
              stopQRPolling();
              setQrStatus(statusRes.status);
              return;
            }
            setQrStatus(statusRes.status === 'scanned' ? 'scanned' : statusRes.status === 'waiting' ? 'waiting' : 'processing');
            qrPollTimer.current = window.setTimeout(poll, 800);
          } catch {
            stopQRPolling();
            setQrStatus('error');
            setQrMessage('检查扫码状态失败，请重新扫码。');
          }
        };
        qrPollTimer.current = window.setTimeout(poll, 800);
      }
    } catch (e) {
      setQrStatus('error');
      setQrMessage('二维码生成失败，请重试。');
    }
  };

  useEffect(() => () => stopQRPolling(), []);

  if (loading) return <div className="p-20 flex justify-center"><Loader2 className="w-8 h-8 text-[#FFE815] animate-spin"/></div>;

  return (
    <div className="space-y-8 animate-fade-in relative">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">账号管理</h2>
          <p className="text-gray-500 mt-2 font-medium">管理您的闲鱼授权账号及设置。</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadRuntimeStatuses}
            className="ios-btn-secondary flex items-center gap-2 px-4 py-3 rounded-2xl font-bold"
            title="刷新账号运行状态"
          >
            <RefreshCw className="w-4 h-4" />
            刷新状态
          </button>
          <button
              onClick={startQRLogin}
              className="ios-btn-primary flex items-center gap-2 px-6 py-3 rounded-2xl font-bold shadow-lg shadow-yellow-200 transition-transform hover:scale-105 active:scale-95"
          >
            <QrCode className="w-5 h-5" />
            扫码添加新账号
          </button>
        </div>
      </div>

      {/* Account Grid */}
      <div className="grid grid-cols-1 gap-6">
        {accounts.map((account) => {
          const runtimeStatus = runtimeStatuses[account.id];
          const statusStyle = statusPresentation(runtimeStatus);
          const StatusIcon = statusStyle.icon;
          return (
          <div key={account.id} className="ios-card p-6 rounded-[2rem] flex items-center justify-between group hover:border-[#FFE815] transition-all duration-300">
            <div className="flex items-center gap-8">
              <div className="relative">
                <img
                  src={account.avatar_url}
                  alt="avatar"
                  className="w-20 h-20 rounded-3xl object-cover shadow-md ring-4 ring-white"
                />
                <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-4 border-white flex items-center justify-center ${account.enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                    {account.enabled && <Check className="w-3 h-3 text-white" />}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-xl font-extrabold text-gray-900">{account.nickname || account.remark || `账号 ${account.id.substring(0,6)}...`}</h3>
                    <button
                      type="button"
                      onClick={() => runtimeStatus && setStatusDetails({ account, status: runtimeStatus })}
                      disabled={!runtimeStatus}
                      className={`inline-flex items-center gap-1.5 border px-2.5 py-1 rounded-lg text-xs font-bold transition-colors ${statusStyle.className} ${runtimeStatus ? 'hover:brightness-95' : 'cursor-default'}`}
                      title={runtimeStatus ? '查看运行状态详情' : '状态加载中'}
                    >
                      <StatusIcon className="w-3.5 h-3.5" />
                      {runtimeStatus?.label || '状态加载中'}
                    </button>
                    {account.ai_enabled && (
                        <span className="px-2.5 py-0.5 rounded-lg bg-purple-100 text-purple-700 text-xs font-bold flex items-center gap-1">
                          <Bot className="w-3 h-3" /> AI
                        </span>
                    )}
                </div>
                <p className="text-sm text-gray-500 font-medium mb-3">{account.remark || account.note || '暂无备注'}</p>
                <div className="flex gap-2">
                   {account.auto_confirm && <span className="text-xs bg-yellow-50 text-yellow-700 px-3 py-1.5 rounded-lg font-bold flex items-center gap-1.5"><MessageSquare className="w-3 h-3"/> 自动回复</span>}
                   {account.pause_duration > 0 && <span className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg font-bold flex items-center gap-1.5"><Clock className="w-3 h-3"/> 暂停{account.pause_duration}分钟</span>}
                </div>
                <p className="text-xs text-gray-400 mt-3">状态更新：{formatStatusTime(runtimeStatus?.last_updated_at)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
                <button
                    onClick={() => openEditModal(account)}
                    className="p-3 rounded-xl hover:bg-gray-100 transition-colors text-gray-600"
                    title="编辑账号"
                >
                    <Edit2 className="w-5 h-5" />
                </button>
                <button
                    onClick={() => openAIModal(account)}
                    className="p-3 rounded-xl hover:bg-purple-100 transition-colors text-purple-600"
                    title="AI设置"
                >
                    <Bot className="w-5 h-5" />
                </button>
                <button
                    onClick={() => handleToggle(account.id, account.enabled)}
                    className={`p-3 rounded-xl transition-colors ${account.enabled ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                >
                    <Power className="w-5 h-5" />
                </button>
                <button
                    onClick={() => handleDelete(account.id)}
                    className="p-3 rounded-xl hover:bg-red-100 transition-colors text-red-500"
                >
                    <Trash2 className="w-5 h-5" />
                </button>
            </div>
          </div>
          );
        })}

        {accounts.length === 0 && (
            <div className="ios-card p-12 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <User className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">暂无账号</h3>
                <p className="text-gray-500 mt-1">请点击右上角扫码添加您的闲鱼账号</p>
            </div>
        )}
      </div>

      {statusDetails && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <div>
                <h3 className="text-xl font-extrabold text-gray-900">账号运行状态</h3>
                <p className="text-sm text-gray-500 mt-1">{statusDetails.account.nickname || statusDetails.account.remark || statusDetails.account.id}</p>
              </div>
              <button onClick={() => setStatusDetails(null)} disabled={verificationActionBusy} className="p-2 rounded-xl hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40" title="关闭"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="modal-body space-y-5">
              <div className={`border rounded-xl p-4 ${statusPresentation(statusDetails.status).className}`}>
                <div className="font-bold">{statusDetails.status.label}</div>
                <p className="text-sm mt-1">{statusDetails.status.reason}</p>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div><dt className="text-gray-500">当前连接</dt><dd className="font-semibold text-gray-900 mt-1">{statusDetails.status.connection_state}</dd></div>
                <div><dt className="text-gray-500">重连次数</dt><dd className="font-semibold text-gray-900 mt-1">{statusDetails.status.reconnect_count}</dd></div>
                <div><dt className="text-gray-500">最近收到买家消息</dt><dd className="font-semibold text-gray-900 mt-1">{formatStatusTime(statusDetails.status.last_message_received_at)}</dd></div>
                <div><dt className="text-gray-500">最近 Token 刷新成功</dt><dd className="font-semibold text-gray-900 mt-1">{formatStatusTime(statusDetails.status.last_token_success_at)}</dd></div>
                <div className="sm:col-span-2"><dt className="text-gray-500">最近状态更新</dt><dd className="font-semibold text-gray-900 mt-1">{formatStatusTime(statusDetails.status.last_updated_at)}</dd></div>
              </dl>
              <div className={`rounded-xl p-4 text-sm ${statusDetails.status.ai_reply_ready ? 'bg-emerald-50 border border-emerald-100 text-emerald-800' : 'bg-gray-50 border border-gray-200 text-gray-700'}`}>
                <span className="font-bold">AI 自动回复：</span>{statusDetails.status.ai_reply_reason}
              </div>
              <div className="bg-blue-50 border border-blue-100 text-blue-800 rounded-xl p-4 text-sm leading-6">
                <span className="font-bold">建议操作：</span>{statusDetails.status.recommendation}
              </div>
              {(statusDetails.status.status === 'authorization_required' || statusDetails.status.status === 'connection_error') && (
                <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm leading-6 text-sky-900">
                  <p>{statusDetails.status.status === 'authorization_required'
                    ? '完成闲鱼官方客户端的重新授权后，可执行一次受控探测。授权仍未恢复时，账号会继续保持停止状态。'
                    : '检查网络后，可执行一次受控探测。连接仍不可用时，账号会继续保持停止状态。'
                  }</p>
                  <button
                    type="button"
                    onClick={reconnectAfterAuthorization}
                    disabled={verificationActionBusy}
                    className="mt-3 flex w-full min-w-0 items-center justify-center gap-2 whitespace-normal rounded-xl bg-sky-700 px-4 py-3 text-center font-bold text-white transition-colors hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {verificationResumeBusy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <RefreshCw className="h-4 w-4 shrink-0" />}
                    <span>{statusDetails.status.status === 'connection_error' ? '执行一次受控探测' : '手工重新连接'}</span>
                  </button>
                </div>
              )}
              {statusDetails.status.status === 'verification_required' && (
                <div className="space-y-3">
                  {statusDetails.status.manual_verification_state === 'network_blocked' ? (
                    <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm leading-6 text-orange-900">
                      {statusDetails.status.manual_verification_message}
                      <p className="mt-2">请等待平台拦截冷却，并在常用网络和闲鱼官方客户端中处理正常的账号提示后，再刷新状态。容器出口网络被拦截时，无法在此页面完成账号验证。</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm leading-6 text-gray-600">验证页面仅用于完成平台要求的人工操作，完成后系统会重新连接该账号。</p>
                       <button
                         type="button"
                         onClick={() => openManualVerification(statusDetails.account)}
                         disabled={(
                           !statusDetails.status.manual_verification_available
                           && statusDetails.status.manual_verification_state !== 'interactive_unavailable'
                         ) || verificationActionBusy}
                        className="ios-btn-primary w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {verificationBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                         打开 Windows 验证窗口
                      </button>
                      {!statusDetails.status.manual_verification_available && (
                        <>
                          <p className="text-xs leading-5 text-orange-800">{statusDetails.status.manual_verification_message || '当前验证会话不可用。'}</p>
                          <button type="button" onClick={requestManualVerification} disabled={verificationActionBusy} className="mt-3 w-full rounded-xl border border-orange-300 bg-white px-4 py-3 text-sm font-bold text-orange-800 transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-50">
                            {verificationBusy ? '正在请求验证页面' : '请求新的验证页面'}
                          </button>
                        </>
                      )}
                    </>
                  )}
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-900">
                    <p>已在闲鱼官方客户端处理完平台提示后，可在这里恢复账号运行。</p>
                    <button
                      type="button"
                      onClick={resumeAfterVerification}
                      disabled={verificationActionBusy}
                      className="mt-3 flex w-full min-w-0 items-center justify-center gap-2 whitespace-normal rounded-xl bg-emerald-600 px-4 py-3 text-center font-bold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {verificationResumeBusy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <CircleCheck className="h-4 w-4 shrink-0" />}
                      <span>人工恢复账号</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>, document.body
      )}

      {manualVerification && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container" style={{ width: 'min(560px, calc(100vw - 32px))', borderRadius: '8px' }}>
            <div className="modal-header">
              <div>
                <h3 className="text-xl font-extrabold text-gray-900">人工验证</h3>
                <p className="text-sm text-gray-500 mt-1">{manualVerification.account.nickname || manualVerification.account.remark || manualVerification.account.id}</p>
              </div>
              <button onClick={closeManualVerification} disabled={verificationBusy} className="p-2 rounded-xl hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40" title="关闭"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="modal-body space-y-6">
              <p className="text-sm leading-6 text-gray-600">{manualVerification.session.message}</p>
              <div className="border border-gray-200 bg-gray-50 px-4 py-5 text-sm leading-6 text-gray-600" style={{ borderRadius: '8px' }}>
                验证窗口已单独打开。请在该窗口中直接拖动滑块；完成后回到这里继续。
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={closeManualVerification} disabled={verificationBusy} className="ios-btn-secondary px-4 py-2.5 rounded-xl font-bold disabled:cursor-not-allowed disabled:opacity-50">取消</button>
                <button type="button" onClick={finishManualVerification} disabled={verificationBusy} className="ios-btn-primary flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold disabled:opacity-50">
                  {verificationBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                  完成并重新连接
                </button>
              </div>
            </div>
          </div>
        </div>, document.body
      )}

      {/* QR Code Modal */}
      {showQRModal && createPortal(
          <div className="modal-overlay-centered">
              <div className="modal-container" style={{maxWidth: '24rem'}}>
                  <button
                    onClick={closeQRModal}
                    className="self-end p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors mb-6"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>

                  <div className="modal-body">
                      <div className="text-center">
                          <h3 className="text-2xl font-extrabold text-gray-900 mb-2">扫码登录</h3>
                           <p className="text-gray-500 mb-8 font-medium">
                             {qrStatus === 'processing' ? '已扫码，正在保存授权信息' : qrStatus === 'scanned' ? '已扫描，请在闲鱼 App 确认登录' : '请打开闲鱼 App 扫描下方二维码'}
                           </p>

                          <div className="w-64 h-64 bg-[#F7F8FA] rounded-[2rem] mx-auto flex items-center justify-center overflow-hidden border-4 border-white shadow-inner mb-8 relative">
                              {qrStatus === 'loading' && <Loader2 className="w-10 h-10 text-[#FFE815] animate-spin" />}
                               {(qrStatus === 'waiting' || qrStatus === 'scanned') && <img src={qrCodeUrl} alt="QR Code" className="w-full h-full p-2" />}
                               {qrStatus === 'processing' && (
                                   <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center text-gray-700 animate-fade-in">
                                      <Loader2 className="w-9 h-9 text-[#d4b900] animate-spin mb-4" />
                                      <span className="font-bold text-lg">正在保存授权</span>
                                   </div>
                               )}
                              {qrStatus === 'success' && (
                                  <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center text-green-600 animate-fade-in">
                                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                         <Check className="w-8 h-8" />
                                      </div>
                                      <span className="font-bold text-lg">登录成功</span>
                                  </div>
                              )}
                               {qrStatus === 'error' && (
                                  <div className="flex flex-col items-center">
                                      <span className="text-red-500 font-bold mb-2">获取失败</span>
                                      <button onClick={startQRLogin} className="text-xs bg-gray-200 px-3 py-1 rounded-full flex items-center gap-1 hover:bg-gray-300"><RefreshCw className="w-3 h-3"/> 重试</button>
                                  </div>
                               )}
                               {qrStatus === 'verification_required' && (
                                   <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center text-orange-700 animate-fade-in px-6 text-center">
                                      <AlertTriangle className="w-9 h-9 mb-4" />
                                      <span className="font-bold">需要在闲鱼客户端完成验证</span>
                                   </div>
                               )}
                           </div>

                           <p className="text-xs text-gray-500 font-medium bg-gray-50 py-2 px-3 rounded-xl leading-5">{qrMessage || '二维码有效期为5分钟，请尽快扫码。'}</p>
                      </div>
                  </div>
              </div>
          </div>,
          document.body
      )}

      {/* 编辑账号弹窗 */}
      {activeModal === 'edit' && editingAccount && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container" style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <div>
                <h3 className="text-2xl font-extrabold text-gray-900">编辑账号</h3>
                <p className="text-sm text-gray-500 mt-1">{editingAccount.nickname || editingAccount.remark || editingAccount.id}</p>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="modal-body space-y-6">
              {/* 账号ID */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">账号ID</label>
                <input
                  type="text"
                  value={editingAccount.id}
                  disabled
                  className="w-full ios-input px-4 py-3 rounded-xl bg-gray-50 text-gray-500"
                />
              </div>

              {/* 备注 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">备注</label>
                <input
                  type="text"
                  value={editForm.remark}
                  onChange={(e) => setEditForm({ ...editForm, remark: e.target.value })}
                  placeholder="为账号添加备注"
                  className="w-full ios-input px-4 py-3 rounded-xl"
                />
              </div>

              {/* Cookie */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Cookie</label>
                <textarea
                  value={editForm.cookie}
                  onChange={(e) => setEditForm({ ...editForm, cookie: e.target.value })}
                  placeholder="更新账号Cookie"
                  className="w-full ios-input px-4 py-3 rounded-xl h-32 resize-none font-mono text-xs"
                />
                <p className="text-xs text-gray-500 mt-1">当前Cookie长度: {editForm.cookie.length} 字符</p>
              </div>

              {/* 自动确认收货 */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <div className="font-bold text-gray-900 flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    自动确认收货
                  </div>
                  <div className="text-xs text-gray-500">自动点击确认收货按钮</div>
                </div>
                <button
                  type="button"
                  onClick={() => setEditForm({ ...editForm, auto_confirm: !editForm.auto_confirm })}
                  className={`xsb-switch ${
                    editForm.auto_confirm ? 'bg-[#FFE815]' : 'bg-gray-300'
                  }`}
                  data-state={editForm.auto_confirm ? 'on' : 'off'}
                >
                  <span className="xsb-switch-thumb" />
                </button>
              </div>

              {/* 暂停时长 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  暂停处理时长（分钟）
                </label>
                <input
                  type="number"
                  value={editForm.pause_duration}
                  onChange={(e) => setEditForm({ ...editForm, pause_duration: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                  min="0"
                  max="120"
                  className="w-full ios-input px-4 py-3 rounded-xl"
                />
                <p className="text-xs text-gray-500 mt-1">{pauseResumeHint}</p>
              </div>

              {/* 登录信息 */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Key className="w-5 h-5 text-amber-500" />
                  登录信息
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">用户名</label>
                    <input
                      type="text"
                      value={editForm.username}
                      onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                      placeholder="闲鱼账号/手机号"
                      className="w-full ios-input px-4 py-3 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">登录密码</label>
                    <div className="relative">
                      <input
                        type={editForm.showLoginPassword ? 'text' : 'password'}
                        value={editForm.login_password}
                        onChange={(e) => setEditForm({ ...editForm, login_password: e.target.value })}
                        placeholder="用于自动登录"
                        className="w-full ios-input px-4 py-3 rounded-xl pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, showLoginPassword: !editForm.showLoginPassword })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {editForm.showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-gray-900">登录时显示浏览器</div>
                      <div className="text-xs text-gray-500">调试时可开启查看登录过程</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditForm({ ...editForm, show_browser: !editForm.show_browser })}
                      className={`xsb-switch ${
                        editForm.show_browser ? 'bg-[#FFE815]' : 'bg-gray-300'
                      }`}
                      data-state={editForm.show_browser ? 'on' : 'off'}
                    >
                      <span className="xsb-switch-thumb" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  disabled={saving}
                >
                  取消
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 ios-btn-primary px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* AI设置弹窗 */}
      {activeModal === 'ai-settings' && editingAccount && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container" style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <div>
                <h3 className="text-2xl font-extrabold text-gray-900 flex items-center gap-2">
                  <Bot className="w-6 h-6 text-purple-500" />
                  AI助手设置
                </h3>
                <p className="text-sm text-gray-500 mt-1">{editingAccount.nickname || editingAccount.remark || editingAccount.id}</p>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="modal-body space-y-6">
              {/* 启用AI */}
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl">
                <div>
                  <div className="font-bold text-gray-900 flex items-center gap-2">
                    <Bot className="w-4 h-4 text-purple-500" />
                    启用AI自动回复
                  </div>
                  <div className="text-xs text-gray-500">AI将自动处理买家的砍价消息</div>
                </div>
                <button
                  type="button"
                  onClick={() => setAiSettings({ ...aiSettings, ai_enabled: !aiSettings.ai_enabled })}
                  className={`xsb-switch ${
                    aiSettings.ai_enabled ? 'bg-[#FFE815]' : 'bg-gray-300'
                  }`}
                  data-state={aiSettings.ai_enabled ? 'on' : 'off'}
                >
                  <span className="xsb-switch-thumb" />
                </button>
              </div>

              {/* 模型连接设置 */}
              <div className="border-t border-gray-200 pt-6 space-y-4">
                <h3 className="text-lg font-bold text-gray-900">模型连接</h3>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">模型名称</label>
                  <input
                    type="text"
                    value={aiSettings.model_name}
                    onChange={(e) => setAiSettings({ ...aiSettings, model_name: e.target.value })}
                    placeholder="例如：gpt-4.1-mini、qwen-plus 或您自己的模型名称"
                    className="w-full ios-input px-4 py-3 rounded-xl"
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500 mt-1">普通文本输入，按您的模型服务商实际模型名填写。</p>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">模型接口地址</label>
                  <input
                    type="url"
                    value={aiSettings.base_url}
                    onChange={(e) => setAiSettings({ ...aiSettings, base_url: e.target.value })}
                    placeholder="例如：https://api.example.com/v1"
                    className="w-full ios-input px-4 py-3 rounded-xl"
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500 mt-1">填写 OpenAI 兼容接口的 Base URL。模型凭据由本机部署配置读取，不在页面保存。</p>
                </div>
              </div>

              {/* 砍价策略 */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">砍价策略</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">最大折扣比例 (%)</label>
                    <input
                      type="number"
                      value={aiSettings.max_discount_percent}
                      onChange={(e) => setAiSettings({ ...aiSettings, max_discount_percent: parseInt(e.target.value) || 0 })}
                      className="w-full ios-input px-4 py-3 rounded-xl"
                      min="0"
                      max="100"
                    />
                    <p className="text-xs text-gray-500 mt-1">例如：10表示最多降价10%</p>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">最大折扣金额 (元)</label>
                    <input
                      type="number"
                      value={aiSettings.max_discount_amount}
                      onChange={(e) => setAiSettings({ ...aiSettings, max_discount_amount: parseInt(e.target.value) || 0 })}
                      className="w-full ios-input px-4 py-3 rounded-xl"
                      min="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">例如：100表示最多降价100元</p>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">最大砍价轮次</label>
                    <input
                      type="number"
                      value={aiSettings.max_bargain_rounds}
                      onChange={(e) => setAiSettings({ ...aiSettings, max_bargain_rounds: parseInt(e.target.value) || 1 })}
                      className="w-full ios-input px-4 py-3 rounded-xl"
                      min="1"
                      max="10"
                    />
                    <p className="text-xs text-gray-500 mt-1">买家最多可以砍价的次数</p>
                  </div>
                </div>
              </div>

              {/* 提示词配置 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">账号专用提示词（可选）</label>
                <textarea
                  value={aiSettings.custom_prompts}
                  onChange={(e) => setAiSettings({ ...aiSettings, custom_prompts: e.target.value })}
                  placeholder="输入店铺统一的回复规则...&#10;&#10;例如：回复简洁自然；优先说明商品真实情况；不承诺未确认的发货时间；遇到售后、退款或联系方式问题请转人工。"
                  className="w-full ios-input px-4 py-3 rounded-xl h-40 resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">留空时使用系统设置中的提示词；填写后仅对当前账号生效。生成时会结合商品信息、客户问题、聊天上下文和议价限制。</p>
              </div>

              {/* AI如何工作 */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  AI如何工作
                </h4>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• 结合商品信息、客户问题和最近聊天上下文生成回复</li>
                  <li>• 按提示词配置和议价限制控制沟通风格与价格边界</li>
                  <li>• 退款、售后、联系方式等敏感问题不会自动回复</li>
                </ul>
              </div>
            </div>

            <div className="modal-footer">
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  disabled={saving}
                >
                  取消
                </button>
                <button
                  onClick={handleSaveAISettings}
                  className="flex-1 ios-btn-primary px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AccountList;
